import { mcpAccessFor } from '@voll-intelligence/domain';

import type { MemberDetail, MemberRole } from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  AppIcons,
  Avatar,
  AvatarFallback,
  IconButton,
  Switch,
  type AppIconName,
} from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';

import { DomainChip } from './domain_chip';
import { StatusChip } from './status_chip';

interface MembersDrawerProps {
  member: MemberDetail;
  /**
   * Mirrors the backend `accounts:members:role-write` permission (owner-only).
   * When false the role cards render disabled so non-owners can still see the
   * current role but cannot trigger a change.
   */
  canChangeRole: boolean;
  /** Mirrors `accounts:members:status-write` — gates Suspend and Resume. */
  canChangeStatus: boolean;
  /** Mirrors `accounts:members:remove`. */
  canRemove: boolean;
  /** Mirrors `accounts:members:features-write` — gates the MCP access switch. */
  canGrantMcp: boolean;
  onClose: () => void;
  onChangeRole: (userId: string, role: MemberRole) => void;
  onSetStatus: (userId: string, status: 'active' | 'suspended') => void;
  onRemove: (userId: string) => void;
  onSetMcpAccess: (userId: string, enabled: boolean) => void;
}

const ROLE_ICONS: Record<MemberRole, AppIconName> = {
  guest: 'user',
  member: 'user-check',
  admin: 'shield-check',
  owner: 'crown',
};

const ROLES: MemberRole[] = ['guest', 'member', 'admin', 'owner'];

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MemberHeader({
  member,
  onClose,
}: Readonly<{ member: MemberDetail; onClose: () => void }>): React.JSX.Element {
  return (
    <header className="border-b border-border px-5 pb-3.5 pt-4.5">
      <div className="flex items-center gap-3">
        <Avatar size="lg">
          <AvatarFallback displayName={member.name} fallback="·" />
        </Avatar>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 font-serif text-xl font-normal leading-tight tracking-tight text-foreground">
            {member.name}
          </h2>
          <div className="mt-0.5 truncate font-mono text-micro text-muted-foreground">
            {member.email}
          </div>
        </div>
        <IconButton
          icon="close"
          variant="ghost"
          size="sm"
          onClick={onClose}
          title={t('members.drawer.closeTitle')}
          aria-label={t('members.drawer.closeTitle')}
        />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <StatusChip status={member.status} />
        <DomainChip domain={member.domain} />
        {member.source === 'jit' ? (
          <span className="inline-flex h-5.5 items-center gap-1 rounded-pill border border-border bg-card px-2 text-micro text-muted-foreground">
            <AppIcons name="lightning" size={11} />
            {t('members.drawer.viaJit')}
          </span>
        ) : null}
      </div>
    </header>
  );
}

export function MembersDrawer({
  member,
  canChangeRole,
  canChangeStatus,
  canRemove,
  canGrantMcp,
  onClose,
  onChangeRole,
  onSetStatus,
  onRemove,
  onSetMcpAccess,
}: Readonly<MembersDrawerProps>): React.JSX.Element {
  const isSuspended = member.status === 'suspended';

  return (
    <aside className="sticky top-topbar-h flex h-[calc(100vh-var(--topbar-h))] min-h-0 flex-col overflow-hidden border-l border-border bg-card">
      <MemberHeader member={member} onClose={onClose} />

      <div className="flex-1 overflow-y-auto px-5 pb-6 pt-1.5">
        <section className="border-b border-border py-3.5">
          <SectionTitle>{t('members.drawer.role.title')}</SectionTitle>
          <div className="grid grid-cols-2 gap-1.5">
            {ROLES.map((r) => {
              const active = member.role === r;
              return (
                <button
                  key={r}
                  type="button"
                  disabled={!canChangeRole}
                  onClick={() => {
                    onChangeRole(member.userId, r);
                  }}
                  className={twcn(
                    'flex flex-col gap-0.5 rounded-md border border-border bg-card p-2.5 text-left text-foreground transition-[border-color,background,box-shadow] duration-fast ease-standard',
                    canChangeRole
                      ? 'cursor-pointer hover:border-border-strong'
                      : 'cursor-not-allowed opacity-60',
                    active && 'border-primary bg-primary-tint shadow-inset-primary',
                  )}
                >
                  <span className="flex items-center gap-1.5 text-meta font-medium">
                    <AppIcons name={ROLE_ICONS[r]} size={11} />
                    {t(`members.role.${r}`)}
                  </span>
                  <span
                    className={twcn(
                      'text-card-id text-muted-foreground',
                      active && 'text-primary-ink/85',
                    )}
                  >
                    {t(`members.role.${r}.desc`)}
                  </span>
                </button>
              );
            })}
          </div>
          {member.promotedBy && member.promotedAt ? (
            <div className="mt-2.5 text-micro text-muted-foreground">
              <AppIcons name="check" size={11} className="mr-1 inline-block align-middle" />
              {t('members.drawer.role.promotedBy', {
                name: member.promotedBy,
                date: fmtDateTime(member.promotedAt),
              })}
            </div>
          ) : null}
        </section>

        <section className="border-b border-border py-3.5">
          <SectionTitle>{t('members.drawer.mcp.title')}</SectionTitle>
          <div className="flex items-center justify-between gap-3">
            <div className="text-micro text-muted-foreground">
              {member.role === 'owner'
                ? t('members.drawer.mcp.ownerAlways')
                : member.role === 'guest'
                  ? t('members.drawer.mcp.guestNever')
                  : t('members.drawer.mcp.sub')}
            </div>
            <Switch
              checked={mcpAccessFor(member.role, member.features)}
              disabled={member.role === 'owner' || member.role === 'guest' || !canGrantMcp}
              onCheckedChange={(v) => {
                onSetMcpAccess(member.userId, v);
              }}
              aria-label={t('members.drawer.mcp.switchLabel')}
              title={t('members.drawer.mcp.switchLabel')}
            />
          </div>
        </section>

        <section className="border-b border-border py-3.5">
          <SectionTitle>{t('members.drawer.origin.title')}</SectionTitle>
          <DrawerRow label={t('members.drawer.origin.domainLabel')}>
            <span className="font-mono text-card-id text-foreground">{member.domain}</span>
          </DrawerRow>
          <DrawerRow label={t('members.drawer.origin.jumpcloudLabel')}>
            <span className="font-mono text-card-id text-foreground">{member.jumpcloudId}</span>
          </DrawerRow>
          <DrawerRow label={t('members.drawer.origin.createdLabel')}>
            <span className="text-meta text-foreground">{fmtDateTime(member.joinedAt)}</span>
          </DrawerRow>
        </section>

        <section className="py-3.5">
          <SectionTitle>{t('members.drawer.danger.title')}</SectionTitle>
          {isSuspended ? (
            <DangerRow
              title={t('members.drawer.danger.resume.title')}
              sub={
                (member.suspendedReason ?? t('members.drawer.danger.resume.subFallback')) +
                t('members.drawer.danger.resume.subAfter')
              }
              icon="play-circle"
              label={t('members.drawer.danger.resume.btn')}
              variant="resume"
              disabled={!canChangeStatus}
              onClick={() => {
                onSetStatus(member.userId, 'active');
              }}
            />
          ) : (
            <DangerRow
              title={t('members.drawer.danger.suspend.title')}
              sub={t('members.drawer.danger.suspend.sub')}
              icon="pause-circle"
              label={t('members.drawer.danger.suspend.btn')}
              variant="suspend"
              disabled={!canChangeStatus}
              onClick={() => {
                onSetStatus(member.userId, 'suspended');
              }}
            />
          )}
          <DangerRow
            title={t('members.drawer.danger.remove.title')}
            sub={t('members.drawer.danger.remove.sub')}
            icon="trash"
            label={t('members.drawer.danger.remove.btn')}
            variant="suspend"
            disabled={!canRemove}
            onClick={() => {
              onRemove(member.userId);
            }}
          />
        </section>
      </div>
    </aside>
  );
}

function SectionTitle({ children }: Readonly<{ children: React.ReactNode }>): React.JSX.Element {
  return (
    <div className="mb-2.5 font-mono text-2xs font-medium uppercase tracking-eyebrow-wider text-faint-foreground">
      {children}
    </div>
  );
}

function DrawerRow({
  label,
  children,
}: Readonly<{
  label: string;
  children: React.ReactNode;
}>): React.JSX.Element {
  return (
    <div className="grid grid-cols-row-label items-center gap-3 py-1.5 text-meta">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{children}</span>
    </div>
  );
}

interface DangerRowProps {
  title: string;
  sub: string;
  icon: AppIconName;
  label: string;
  variant: 'suspend' | 'resume';
  disabled?: boolean;
  onClick: () => void;
}

function DangerRow({
  title,
  sub,
  icon,
  label,
  variant,
  disabled,
  onClick,
}: Readonly<DangerRowProps>): React.JSX.Element {
  let cursorClass: string;
  if (disabled) {
    cursorClass = 'cursor-not-allowed opacity-60';
  } else if (variant === 'resume') {
    cursorClass = 'cursor-pointer hover:bg-tone-green-tint';
  } else {
    cursorClass = 'cursor-pointer hover:bg-tone-red-tint';
  }

  return (
    <div
      className={twcn(
        'mb-2 grid grid-cols-[1fr_auto] items-center gap-3 rounded-md p-3 last:mb-0',
        variant === 'resume'
          ? 'border border-tone-green-fg/28 bg-tone-green-tint/30'
          : 'border border-tone-red-fg/24 bg-tone-red-tint/30',
      )}
    >
      <div>
        <div
          className={twcn(
            'text-meta font-medium',
            variant === 'resume' ? 'text-tone-green-ink' : 'text-tone-red-ink',
          )}
        >
          {title}
        </div>
        <div className="mt-0.5 max-w-[32ch] text-micro text-muted-foreground">{sub}</div>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={twcn(
          'inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-meta font-medium transition-colors',
          variant === 'resume'
            ? 'border-tone-green-fg/40 text-tone-green-ink'
            : 'border-tone-red-fg/40 text-tone-red-ink',
          cursorClass,
        )}
      >
        <AppIcons name={icon} size={12} />
        {label}
      </button>
    </div>
  );
}
